/**
 * TestCaseDetailsTab — "Details" tab of the TestCaseDetail pane.
 * Shows description, preconditions, suite (soft-deprecated), folder, timestamps, tags.
 */

import type { TestCaseType } from '../types';
import { CaseTypeSelect } from '../shared/CaseTypeSelect';
import { FolderBreadcrumb } from '../shared/FolderBreadcrumb';
import { TagsComboBox } from '../shared/TagsComboBox';
import { SuiteComboBox } from '../shared/SuiteComboBox';

const TEXTAREA_CLASS =
    'w-full px-3 py-2 text-sm border border-slate-200 dark:border-gh-border-dark rounded-lg ' +
    'bg-white dark:bg-gh-bg-dark text-slate-800 dark:text-slate-200 ' +
    'placeholder-slate-400 dark:placeholder-slate-500 ' +
    'focus:outline-none focus:ring-2 focus:ring-blue-500 transition resize-none';

interface TestCaseDetailsTabProps {
    description:    string;
    setDescription: (v: string) => void;
    preconditions:  string;
    setPreconditions: (v: string) => void;
    suite:          string;
    setSuite:       (v: string) => void;
    caseType:       TestCaseType;
    setCaseType:    (v: TestCaseType) => void;
    tags:           string[];
    setTags:        (v: string[]) => void;
    projectId:      string | null;
    folderPath?:    string;
    createdBy?:     string;
    createdAt?:     string;
}

export function TestCaseDetailsTab({
    description,
    setDescription,
    preconditions,
    setPreconditions,
    suite,
    setSuite,
    caseType,
    setCaseType,
    tags,
    setTags,
    projectId,
    folderPath,
    createdBy,
    createdAt,
}: TestCaseDetailsTabProps) {
    return (
        <div data-testid="test-case-details-tab" className="flex flex-col gap-4 p-4 overflow-y-auto">
            {/* Folder path */}
            {folderPath && (
                <div>
                    <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1">
                        Folder
                    </label>
                    <FolderBreadcrumb path={folderPath} />
                </div>
            )}

            {/* Case Type */}
            <div>
                <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1">
                    Case Type
                </label>
                <CaseTypeSelect value={caseType} onChange={setCaseType} />
            </div>

            {/* Tags */}
            <div>
                <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1">
                    Tags
                </label>
                <TagsComboBox value={tags} onChange={setTags} projectId={projectId} />
            </div>

            {/* Description */}
            <div>
                <label htmlFor="details-description" className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1">
                    Description
                </label>
                <textarea
                    id="details-description"
                    data-testid="details-description-textarea"
                    rows={3}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Describe what this test case covers…"
                    className={TEXTAREA_CLASS}
                />
            </div>

            {/* Preconditions */}
            <div>
                <label htmlFor="details-preconditions" className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1">
                    Preconditions
                </label>
                <textarea
                    id="details-preconditions"
                    data-testid="details-preconditions-textarea"
                    rows={2}
                    value={preconditions}
                    onChange={(e) => setPreconditions(e.target.value)}
                    placeholder="What must be true before running this test?"
                    className={TEXTAREA_CLASS}
                />
            </div>

            {/* Suite */}
            <div>
                <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1">
                    Suite
                </label>
                <SuiteComboBox value={suite} onChange={setSuite} projectId={projectId} />
            </div>

            {/* Audit info */}
            {(createdBy || createdAt) && (
                <div className="text-xs text-slate-400 border-t border-slate-100 dark:border-slate-800 pt-3">
                    {createdBy && <p>Created by: <span className="text-slate-500">{createdBy}</span></p>}
                    {createdAt && (
                        <p>Created: <span className="text-slate-500">
                            {new Date(createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                        </span></p>
                    )}
                </div>
            )}
        </div>
    );
}
